import { useEffect, useState } from 'react'
import { GetStaticPaths } from 'next'
import { getTheatricalRelease } from 'src/services/Theatrical'
import { Durations } from '@/constants/durations'
import { NextPageWithLayout } from '@/layouts'
import { TicketsLayout } from '@/layouts/TicketLayout'
import {
  getTheatricalTrailerExperiment,
  TheatricalTrailerExperimentType,
} from '@/services/CmsService/TheatricalTrailerExperiment'
import { TheatricalReleaseObject, TheatricalReleaseRegion } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { loadTranslations } from '@/utils/translate/translate-server'
import { FreeTicketClaimPartnerView } from '@/views/TicketCheckoutViews/FreeTicketClaimPartnerView'
import { ShowtimesView } from '@/views/TicketCheckoutViews/ShowtimesView'

interface TicketsPageProps {
  theatricalRelease: TheatricalReleaseObject
  theatricalSlug: string
  trailerExperiment: TheatricalTrailerExperimentType
  region: string
  projectGuildScore?: number
}

export const TicketsRegionPage: NextPageWithLayout<TicketsPageProps> = ({
  theatricalSlug,
  theatricalRelease,
  projectGuildScore,
}) => {
  const theatricalReleaseRegion = theatricalRelease.region

  const isTicketClaimRegion = theatricalReleaseRegion?.useSimpleWaitlistOverlay

  const ticketingProviderSlugForRegion = theatricalReleaseRegion?.defaultTicketingProviderSlug

  const { query } = useSafeRouter()
  const freeTicketClaimQuery = query?.promo === 'claim-free-ticket'
  const [showTicketClaim, setShowTicketClaim] = useState(isTicketClaimRegion && freeTicketClaimQuery)

  useEffect(() => {
    setShowTicketClaim(isTicketClaimRegion && freeTicketClaimQuery)
  }, [isTicketClaimRegion, freeTicketClaimQuery])

  const handleOnClaimTicketClick = () => {
    setShowTicketClaim(false)
    if (ticketingProviderSlugForRegion) {
      const queryParams = new URLSearchParams(window.location.search)
      queryParams.set('ticketUrl', ticketingProviderSlugForRegion)
      queryParams.set('selectedVenue', ticketingProviderSlugForRegion)
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`
      window.history.replaceState(null, '', newUrl)
    }
  }

  if (showTicketClaim)
    return (
      <FreeTicketClaimPartnerView
        onClick={handleOnClaimTicketClick}
        theatricalRelease={theatricalRelease}
        theatricalReleaseRegion={theatricalReleaseRegion}
      />
    )

  return (
    <TicketsLayout
      shouldShowLanguageModal
      isDarkMode={true}
      shouldRemoveNavOnMobile={false}
      regions={theatricalRelease?.theatricalReleaseRegions as TheatricalReleaseRegion[]}
    >
      <ShowtimesView
        theatricalRelease={theatricalRelease}
        theatricalSlug={theatricalSlug}
        projectGuildScore={projectGuildScore}
      />
    </TicketsLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/tickets/[slug]/[region]/index.tsx' },
  async ({ federationClient, locale, next404, params, preview, region }) => {
    const theatricalSlug = getItemOrFirstEntry(params.slug)
    if (!theatricalSlug) {
      return next404(Durations.ONE_HOUR_IN_SECONDS)
    }

    const theatricalRelease = await getTheatricalRelease({ theatricalSlug, client: federationClient, region })
    const trailerExperiment = await getTheatricalTrailerExperiment({ slug: theatricalSlug, locale, preview })

    if (!theatricalRelease) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    return {
      props: {
        theatricalSlug,
        theatricalRelease,
        trailerExperiment,
        region,
        ...(await loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account'])),
      },
      revalidate: Durations.TWO_MINUTES_IN_SECONDS,
    }
  },
)

TicketsRegionPage.getLayout = (page) => {
  return page
}
export default TicketsRegionPage
